'use client';

/**
 * Combobox — Shadcn-style combobox (Base UI).
 *
 * Searchable select built on `@base-ui/react/combobox`. Composes InputGroup for the
 * trigger (input + chevron + optional clear button) and styled Portal, Popup, List,
 * Item, Empty, Group, and Collection. Supports single and multiple selection,
 * filtering, and grouped options.
 *
 * @example
 * ```tsx
 * <Combobox.Root items={items}>
 *   <Combobox.Trigger placeholder="Select..." showClear />
 *   <Combobox.Portal>
 *     <Combobox.Backdrop />
 *     <Combobox.Positioner>
 *       <Combobox.Popup>
 *         <Combobox.Empty>No results.</Combobox.Empty>
 *         <Combobox.List>
 *           {(item) => <Combobox.Item key={item} value={item}>{item}<Combobox.ItemIndicator /></Combobox.Item>}
 *         </Combobox.List>
 *       </Combobox.Popup>
 *     </Combobox.Positioner>
 *   </Combobox.Portal>
 * </Combobox.Root>
 * ```
 *
 * In normal implementation a backdrop is not necessary; use `Combobox.Backdrop` only when you need a modal overlay.
 * `Combobox.Popup` shows an arrow pointing at the trigger by default; set `showArrow={false}` to hide it.
 *
 * @see https://ui.shadcn.com/docs/components/combobox (reference only)
 * @see https://base-ui.com/react/components/combobox (Base UI API)
 */
import { Combobox as BaseCombobox } from '@base-ui/react/combobox';
import * as stylex from '@stylexjs/stylex';
import { ChevronDown, X } from 'lucide-react';
import { formStyles } from '../Form/form.styles';
import '../Form/form.tokens.stylex';
import { formTokens } from '../Form/form.tokens.stylex';
import { InputGroup } from '../Form/Fields/primitive/InputGroup';
import { resets } from '../../styles/resets';
import '../../styles/tokens.stylex';
import { borderRadius, color, easing, fontWeights, negativeSpacing, shadow, size, spacing, timing, typographyColor } from '../../styles/tokens.stylex';
const INPUT_GROUP_CONTROL_SLOT = 'input-group-control';
type BaseUICompositeType<T> = {
  styles?: stylex.StyleXStyles;
} & T;
export type ComboboxRootProps<Value = string, Multiple extends boolean | undefined = false> = BaseCombobox.Root.Props<Value, Multiple> & {
  /** Optional style overrides. Root does not render an element; pass styles to Trigger or Popup for layout. */
  styles?: stylex.StyleXStyles;
};

/** Props for the combobox trigger (input + addons). Renders an InputGroup with Combobox.Input, chevron, and optional clear. */
export type ComboboxTriggerProps = BaseCombobox.Input.Props & {
  styles?: stylex.StyleXStyles;
  /** Show clear button when value is present. */
  showClear?: boolean;
};
export type ComboboxPortalProps = BaseUICompositeType<BaseCombobox.Portal.Props>;
export type ComboboxBackdropProps = BaseUICompositeType<BaseCombobox.Backdrop.Props>;
export type ComboboxPositionerProps = BaseUICompositeType<BaseCombobox.Positioner.Props>;
export type ComboboxArrowProps = BaseUICompositeType<BaseCombobox.Arrow.Props>;
export type ComboboxPopupProps = BaseUICompositeType<BaseCombobox.Popup.Props> & {
  /** When true (default), render an arrow pointing at the trigger. Set to false to hide. */
  showArrow?: boolean;
};
export type ComboboxListProps = BaseUICompositeType<BaseCombobox.List.Props>;
export type ComboboxItemProps = BaseUICompositeType<BaseCombobox.Item.Props>;
export type ComboboxItemIndicatorProps = BaseUICompositeType<BaseCombobox.ItemIndicator.Props>;
export type ComboboxEmptyProps = BaseUICompositeType<BaseCombobox.Empty.Props>;
export type ComboboxGroupProps = BaseUICompositeType<BaseCombobox.Group.Props>;
export type ComboboxGroupLabelProps = BaseUICompositeType<BaseCombobox.GroupLabel.Props>;
export type ComboboxClearProps = BaseUICompositeType<BaseCombobox.Clear.Props>;
export type ComboboxCollectionProps = BaseUICompositeType<BaseCombobox.Collection.Props>;
export type ComboboxValueProps = BaseUICompositeType<BaseCombobox.Value.Props>;
export type ComboboxTriggerButtonProps = BaseUICompositeType<BaseCombobox.Trigger.Props>;
export type ComboboxPopupInputProps = BaseUICompositeType<BaseCombobox.Input.Props>;
export type ComboboxInputProps = BaseUICompositeType<BaseCombobox.Input.Props>;

// Root does not render an element; styles prop accepted for API consistency but not applied.
function ComboboxRoot<Value = string, Multiple extends boolean | undefined = false>({
  children,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Root has no single element to attach styles to
  styles: _styles,
  ...props
}: ComboboxRootProps<Value, Multiple>) {
  return <BaseCombobox.Root {...props}>{children}</BaseCombobox.Root>;
}
function ComboboxTrigger({
  styles,
  showClear = false,
  ...props
}: ComboboxTriggerProps) {
  return <InputGroup.Root {...stylex.props(sx.triggerRoot, styles)}>
      <BaseCombobox.Input data-slot={INPUT_GROUP_CONTROL_SLOT} {...stylex.props(formStyles.input, formStyles.inputEditable, sx.input, sx.inputControl)} {...props} />
      <InputGroup.Addon align="inline-end">
        <ChevronDown className="xi66jyo x2lah0s xlzyvqe x15vhz18" aria-hidden />
      </InputGroup.Addon>
      {showClear ? <InputGroup.Addon align="inline-end">
          <BaseCombobox.Clear render={<InputGroup.Button aria-label="Clear value" />} {...{}}>
            <X size={16} aria-hidden />
          </BaseCombobox.Clear>
        </InputGroup.Addon> : null}
    </InputGroup.Root>;
}
function ComboboxPortal({
  styles,
  children,
  ...rest
}: ComboboxPortalProps) {
  return <BaseCombobox.Portal {...stylex.props(sx.portal, styles)} {...rest}>
      {children}
    </BaseCombobox.Portal>;
}
function ComboboxBackdrop({
  styles,
  ...rest
}: ComboboxBackdropProps) {
  return <BaseCombobox.Backdrop {...stylex.props(sx.backdrop, styles)} {...rest} />;
}
function ComboboxPositioner({
  styles,
  ...rest
}: ComboboxPositionerProps) {
  return <BaseCombobox.Positioner {...stylex.props(sx.positioner, styles)} {...rest} />;
}
function ComboboxArrow({
  styles,
  ...rest
}: ComboboxArrowProps) {
  return <BaseCombobox.Arrow {...stylex.props(sx.arrow, styles)} {...rest} />;
}
function ComboboxPopup({
  styles,
  showArrow = true,
  children,
  ...rest
}: ComboboxPopupProps) {
  return <BaseCombobox.Popup {...stylex.props(sx.popup, styles)} {...rest}>
      {showArrow ? <ComboboxArrow /> : null}
      {children}
    </BaseCombobox.Popup>;
}
function ComboboxList({
  styles,
  ...rest
}: ComboboxListProps) {
  return <BaseCombobox.List {...stylex.props(sx.list, styles)} {...rest} />;
}
function ComboboxItem({
  styles,
  ...rest
}: ComboboxItemProps) {
  return <BaseCombobox.Item {...stylex.props(sx.item, styles)} {...rest} />;
}
function ComboboxItemIndicator({
  styles,
  ...rest
}: ComboboxItemIndicatorProps) {
  return <BaseCombobox.ItemIndicator {...stylex.props(sx.itemIndicator, styles)} {...rest} />;
}
function ComboboxEmpty({
  styles,
  ...rest
}: ComboboxEmptyProps) {
  return <BaseCombobox.Empty {...stylex.props(sx.empty, styles)} {...rest} />;
}
function ComboboxGroup({
  styles,
  ...rest
}: ComboboxGroupProps) {
  return <BaseCombobox.Group {...stylex.props(sx.group, styles)} {...rest} />;
}
function ComboboxGroupLabel({
  styles,
  ...rest
}: ComboboxGroupLabelProps) {
  return <BaseCombobox.GroupLabel {...stylex.props(sx.groupLabel, styles)} {...rest} />;
}
function ComboboxClear({
  styles,
  ...rest
}: ComboboxClearProps) {
  return <BaseCombobox.Clear {...stylex.props(sx.clearButton, styles)} {...rest} />;
}
function ComboboxCollection({
  styles,
  children,
  ...rest
}: ComboboxCollectionProps) {
  return <BaseCombobox.Collection {...stylex.props(sx.collection, styles)} {...rest}>
      {children}
    </BaseCombobox.Collection>;
}

/** Renders the selected value or placeholder. Use with TriggerButton for "input inside popup" pattern. */
function ComboboxValue({
  styles,
  ...rest
}: ComboboxValueProps) {
  return <BaseCombobox.Value {...stylex.props(sx.value, styles)} {...rest} />;
}

/** Button trigger that opens the popup. Use with Value for "input inside popup" (e.g. country selector). */
function ComboboxTriggerButton({
  styles,
  children,
  ...rest
}: ComboboxTriggerButtonProps) {
  return <BaseCombobox.Trigger {...stylex.props(resets.button, sx.triggerButton, styles)} {...rest}>
      {children}
      <ChevronDown className="xi66jyo x2lah0s xlzyvqe x15vhz18" aria-hidden />
    </BaseCombobox.Trigger>;
}

/** Input for use inside the popup (e.g. search). Use with TriggerButton + Value for "input inside popup" pattern. */
function ComboboxPopupInput({
  styles,
  ...rest
}: ComboboxPopupInputProps) {
  return <BaseCombobox.Input {...stylex.props(formStyles.input, formStyles.inputEditable, sx.popupInput, styles)} {...rest} />;
}

/** Styled input for custom InputGroup composition. Use when building your own trigger with InputGroup.Root + Addons. */
function ComboboxInput({
  styles,
  ...rest
}: ComboboxInputProps) {
  return <BaseCombobox.Input data-slot={INPUT_GROUP_CONTROL_SLOT} {...stylex.props(formStyles.input, formStyles.inputEditable, sx.input, sx.inputControl, styles)} {...rest} />;
}

/** Combobox composite: Root, Trigger, Input, TriggerButton, Value, Portal, Backdrop, Positioner, Popup, Arrow, PopupInput, List, Item, ItemIndicator, Empty, Group, GroupLabel, Clear, Collection. */
export const Combobox = {
  Root: ComboboxRoot,
  Trigger: ComboboxTrigger,
  Input: ComboboxInput,
  TriggerButton: ComboboxTriggerButton,
  Value: ComboboxValue,
  Portal: ComboboxPortal,
  Backdrop: ComboboxBackdrop,
  Positioner: ComboboxPositioner,
  Popup: ComboboxPopup,
  Arrow: ComboboxArrow,
  PopupInput: ComboboxPopupInput,
  List: ComboboxList,
  Item: ComboboxItem,
  ItemIndicator: ComboboxItemIndicator,
  Empty: ComboboxEmpty,
  Group: ComboboxGroup,
  GroupLabel: ComboboxGroupLabel,
  Clear: ComboboxClear,
  Collection: ComboboxCollection
};
const sx = {
  triggerRoot: {
    $$css: true
  },
  input: {
    kmVPX3: "xsuw8hi",
    kjBf7l: "x1uczgqu",
    kInvED: "x1wfwxd8",
    k3XXqK: "xaatb59",
    kMeerF: "x1k57tk5",
    k7Eaqz: "xeuugli",
    k8Qsv1: "x1dxsdgl",
    $$css: true
  },
  inputControl: {
    kCS8Yb: "x1r8uery",
    kzQI83: "x1iyjqo2",
    kmuXW: "xs83m0k",
    kayTVb: "x14yy4lh",
    $$css: true
  },
  clearButton: {
    $$css: true
  },
  portal: {
    k7Eaqz: "x80t9vs",
    $$css: true
  },
  backdrop: {
    kpwlN0: "x10a8y8t",
    kmkexE: "x9lqykl",
    kWkggS: "x97tsy9",
    kSiTet: "xz5rk10 x1gqblgl x1llxi74",
    kVAEAm: "xixxii4",
    k6sLGO: "x1larqbn",
    $$css: true
  },
  positioner: {
    kLKAdn: "xktxoap",
    kY2c9j: "x1x85hfe",
    $$css: true
  },
  popup: {
    kmVPX3: "x1u7gven",
    kaIpWk: "xakuvmb",
    kmkexE: "x1gfdhlh",
    kWkggS: "x7d3uan",
    kGVxlE: "x1obh9hw",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kHBbk8: "xc8icb0",
    kSiTet: "x1hc1fzr x1gqblgl x1llxi74",
    k3aq6I: "x3oybdh x1il12ni xtx9oap",
    k6sLGO: "x6my1t9",
    $$css: true
  },
  arrow: {
    kmkexE: "x1gfdhlh",
    kWkggS: "x7d3uan",
    k3aq6I: "xg0pu3j",
    kIxVMA: "x19939vx",
    kZKoxP: "x10jimvq",
    k87sOh: "x13vifvy",
    kzqmXN: "x1xx5fdr",
    $$css: true
  },
  list: {
    kUOVxO: "x1ffismk",
    kLKAdn: "x17zmmep",
    kskxy: "xsovusw",
    kORKVm: "x1odjw0f",
    $$css: true
  },
  item: {
    k8WAf4: "xksb554",
    kg3NbH: "x11bvn50",
    kGNEyG: "x6s0dn4",
    kWkggS: "xjbqb8w x1j2jsat x1m4aret",
    kMwMTN: "x17u3nfo x1xxd4t x1j61h1e",
    kkrTdU: "x1ypdohk",
    k1xSpc: "x78zum5",
    kLWn49: "xo5v014",
    $$css: true
  },
  itemIndicator: {
    kMwMTN: "xrcl841",
    keTefX: "xvc5jky",
    $$css: true
  },
  empty: {
    kmVPX3: "xk642s8",
    kMwMTN: "x1joeokn",
    $$css: true
  },
  group: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    $$css: true
  },
  groupLabel: {
    k8WAf4: "xksb554",
    kg3NbH: "x1ijym52",
    kMwMTN: "x1joeokn",
    k63SB2: "xgvtefl",
    $$css: true
  },
  collection: {
    $$css: true
  },
  value: {
    $$css: true
  },
  triggerButton: {
    kmVPX3: "x1u7gven",
    kVAM5u: "xwsnfrh x27hofa xq6vhgx",
    kaIpWk: "xakuvmb",
    ksu8eU: "x1y0btm7",
    kMzoRj: "x1q469ie",
    kOIVth: "x10f5beq",
    kGNEyG: "x6s0dn4",
    kWkggS: "x1o47r4 xnw149a",
    kMwMTN: "xx9pm7w",
    k1xSpc: "x78zum5",
    k7Eaqz: "xeuugli",
    $$css: true
  },
  popupInput: {
    kmVPX3: "xsuw8hi",
    kjBf7l: "x1uczgqu",
    kInvED: "x1wfwxd8",
    k3XXqK: "xaatb59",
    kMeerF: "x1k57tk5",
    k7Eaqz: "xeuugli",
    kzqmXN: "xh8yej3",
    k8Qsv1: "x1dxsdgl",
    $$css: true
  }
};